import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Factory,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  ClipboardList,
  Star,
  Target,
  BarChart3,
  Building2,
  Leaf,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const GlobalGAP_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-green-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-lime-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <Leaf size={18} className="text-green-400" />
            <span>Agricultural & Sustainability Certification</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-green-400 via-emerald-400 to-lime-400">
            GlobalGAP Certification
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Good Agricultural Practices - Sustainable, Safe, and Socially
            Responsible Farming from Farm to Fork
          </p>
        </motion.div>
      </section>

      {/* What is GlobalGAP */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is GlobalGAP Certification?
          </h2>
          <div className="space-y-6 text-lg text-gray-700">
            <p>
              GlobalGAP (Good Agricultural Practices) is an internationally
              recognized certification standard for sustainable farming
              practices. It ensures that agricultural products are produced with
              safe, environmentally friendly, and socially responsible methods.
              GlobalGAP is designed to promote food safety, environmental
              sustainability, and worker welfare throughout the agricultural
              supply chain, from farm to fork.
            </p>
            <p>
              GlobalGAP certification confirms that an agricultural organization
              has implemented and adhered to Good Agricultural Practices that
              ensure the safety and sustainability of products throughout the
              supply chain. It is increasingly becoming a mandatory requirement
              for suppliers and farmers in global food markets.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Key Features of GlobalGAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: "Sustainable Farming Practices",
                desc: "Ensures farming practices minimize environmental impact and promote long-term sustainability.",
                icon: Leaf,
              },
              {
                title: "Food Safety Assurance",
                desc: "Ensures agricultural products are safe for consumption and free from harmful residues.",
                icon: ShieldCheck,
              },
              {
                title: "Environmental Stewardship",
                desc: "Promotes water conservation, soil management, and reduction of pesticide use.",
                icon: Globe,
              },
              {
                title: "Social Responsibility",
                desc: "Protects worker welfare through fair labor practices and safe working conditions.",
                icon: Users,
              },
              {
                title: "Supply Chain Transparency",
                desc: "Strengthens traceability and accountability from farm to consumer.",
                icon: FileCheck,
              },
              {
                title: "Market Recognition",
                desc: "Provides credibility with retailers, regulators, and consumers globally.",
                icon: Award,
              },
            ].map((feature, i) => {
              const IconComponent = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow"
                >
                  <IconComponent className="w-10 h-10 text-green-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="text-gray-700">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </section>

      {/* Good Agricultural Practice */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            What Is Good Agricultural Practice (GAP)?
          </h2>
          <div className="bg-gradient-to-r from-green-50 to-lime-50 p-8 rounded-lg">
            <p className="text-lg text-gray-700 mb-6">
              Good Agricultural Practices (GAP) refer to a set of principles and
              guidelines aimed at promoting:
            </p>
            <ul className="space-y-3">
              {[
                "Environmental Sustainability - Using natural resources responsibly for future generations",
                "Food Safety - Ensuring products are safe for consumption, free from contaminants",
                "Social Responsibility - Upholding fair labor practices and worker welfare",
                "Economic Viability - Ensuring sustainable farming practices are economically viable",
                "Quality Assurance - Maintaining consistent quality throughout the supply chain",
                "Regulatory Compliance - Meeting national and international standards",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </motion.div>
      </section>

      {/* Importance */}
      <section className="py-16 px-4 bg-gray-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Importance of GlobalGAP Certification for Businesses
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Many retailers require GlobalGAP for supplier approval and product access",
              "Increases consumer trust in product safety and sustainability",
              "Promotes environmentally responsible farming practices",
              "Improves working conditions and worker welfare",
              "Supports compliance with local and international regulations",
              "Provides competitive advantage in global markets",
            ].map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-4"
              >
                <Star className="w-6 h-6 text-lime-500 mt-1 flex-shrink-0" />
                <span className="text-gray-700 text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Significance */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Significance of GlobalGAP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-6">
            GlobalGAP helps organizations:
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              "Demonstrate commitment to sustainable and safe farming",
              "Meet increasing demand for environmentally responsible products",
              "Access international markets where GlobalGAP is required",
              "Improve relationships with buyers, retailers, and consumers",
              "Increase operational efficiency while reducing waste",
              "Support environmental conservation and social welfare",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-green-100 to-lime-100 p-6 rounded-lg"
              >
                <CheckCircle className="w-6 h-6 text-green-600 mb-3" />
                <p className="text-gray-800 font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Advantages */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Advantages of GlobalGAP Certification
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Improved Market Access to global retailers and buyers",
              "Increased Consumer Confidence in product safety and sustainability",
              "Environmental Sustainability reducing ecological footprint",
              "Operational Efficiency improving resource management",
              "Compliance with Regulations across multiple jurisdictions",
              "Enhanced Reputation and brand value",
              "Worker Welfare and fair labor practice assurance",
              "Sustainable Income for farmers and stakeholders",
            ].map((advantage, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 bg-white rounded-lg"
              >
                <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                <span className="text-gray-700">{advantage}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-8 text-gray-900">
            Industries That Require GlobalGAP Certification
          </h2>
          <p className="text-lg text-gray-700 mb-8">
            GlobalGAP certification is essential for organizations in various
            sectors of the agricultural industry:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              "Crop Production",
              "Livestock Farming",
              "Aquaculture",
              "Horticulture and Floriculture",
              "Food Processing and Packaging",
              "Agricultural Supply and Distribution",
              "Beverage Production",
              "Certified Organic Farming",
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05 }}
                className="flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-lime-50 rounded-lg"
              >
                <Factory className="w-5 h-5 text-green-600 flex-shrink-0" />
                <span className="text-gray-700 font-medium">{industry}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* How to Get Certification */}
      <section className="py-16 px-4 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            How to Get GlobalGAP Certification
          </h2>
          <div className="space-y-6">
            {[
              {
                step: 1,
                title: "Self-Assessment",
                desc: "Perform self-assessment to evaluate current practices against GlobalGAP standards",
              },
              {
                step: 2,
                title: "Documentation",
                desc: "Develop a comprehensive system for managing food safety and sustainability",
              },
              {
                step: 3,
                title: "Farm Inspection",
                desc: "Undergo farm inspection by an accredited certification body",
              },
              {
                step: 4,
                title: "Employee Training",
                desc: "Train farm workers and management on sustainable practices and safety",
              },
              {
                step: 5,
                title: "Certification Audit",
                desc: "Undergo external audit to verify compliance with GlobalGAP standards",
              },
              {
                step: 6,
                title: "Continual Improvement",
                desc: "Regularly review and improve agricultural practices",
              },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="flex gap-6 items-start"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-green-500 to-lime-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 via-emerald-50 to-lime-50">
        <motion.div
          initial="hidden"
          whileInView="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="max-w-5xl mx-auto"
        >
          <h2 className="text-4xl font-bold mb-12 text-gray-900 text-center">
            Why Choose MBA Solutions for GlobalGAP Certification?
          </h2>
          <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
            <p className="text-lg text-gray-700 mb-6">
              MBA Solutions Pvt. Ltd. provides expert GlobalGAP consultancy
              services, helping agricultural organizations implement and
              maintain sustainable farming practices that ensure food safety,
              environmental stewardship, and worker welfare.
            </p>
            <p className="text-lg text-gray-700 mb-6">Our services include:</p>
            <ul className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "GlobalGAP Gap Analysis and assessment",
                "Sustainability and Food Safety Planning",
                "Farm Inspections and Audits",
                "Employee Training and Awareness Programs",
                "Certification Body Coordination and support",
                "Continual Improvement and monitoring",
              ].map((service, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{service}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-700 mb-6">
              We help agricultural businesses achieve sustainable, safe, and
              compliant farming practices through expert GlobalGAP
              implementation.
            </p>
            <div className="border-t pt-6 mt-6">
              <p className="text-gray-800 font-semibold mb-4">Get in Touch:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Email:</strong> atif@mbasolutions.co
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Globe className="w-5 h-5 text-green-600" />
                  <span className="text-gray-700">
                    <strong>Website:</strong> www.mbasolutions.co
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      <Footer />
    </>
  );
};

export default GlobalGAP_Details;
